import { Button } from "@/core/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/core/components/ui/dialog";
import { Stack } from "@/core/components/ui/stack";
import { toast } from "@/core/components/ui/toast";
import { Typography } from "@/core/components/ui/typography";
import { useBusinessSettingsSidebar } from "@/business-settings/context/business-settings-sidebar-context";
import { useDeleteUser } from "@/business-settings/users/context/delete-user-context";
import { useBusiness } from "@/core/business";
import { getUserFullName } from "@/core/utils/name";
import { useDeleteUser as useDeleteUserMutation } from "@/user/query";
import { DetailedUser } from "@/user/user";

interface DeleteUserConfirmationModalProps {
  users: DetailedUser[];
}

export const DeleteUserConfirmationModal = ({
  users,
}: DeleteUserConfirmationModalProps) => {
  const { businessId } = useBusiness();
  const { deleteUserId, setDeleteUserId } = useDeleteUser();
  const { resetSidebar } = useBusinessSettingsSidebar();
  const { mutate: deleteUser, isPending: isDeleting } =
    useDeleteUserMutation(businessId);

  const userToDelete = users.find((user) => user.id === deleteUserId);
  const isOpen = !!userToDelete;
  const userName = userToDelete
    ? getUserFullName(userToDelete) || userToDelete.email
    : "";

  const handleConfirm = () => {
    if (!deleteUserId) return;

    deleteUser(deleteUserId, {
      onSuccess: () => {
        toast.success("User deleted successfully");
        setDeleteUserId(null);
        resetSidebar();
      },
      onError: (error) => {
        toast.error("Failed to delete user", {
          description:
            error instanceof Error ? error.message : "Please try again",
        });
      },
    });
  };

  const handleCancel = () => {
    setDeleteUserId(null);
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleCancel}>
      <DialogContent
        description={`Are you sure you want to delete ${userName}? This action cannot be undone.`}
      >
        <DialogHeader>
          <DialogTitle>Delete User</DialogTitle>
        </DialogHeader>
        <Stack className="p-6 pt-0" gap="lg" fullWidth>
          <Typography variant="bodyW1" textColor="secondary">
            Are you sure you want to delete <b>{userName}</b>? This action
            cannot be undone.
          </Typography>
        </Stack>
        <DialogFooter>
          <Button
            variant="secondary"
            onClick={handleCancel}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button onClick={handleConfirm} loading={isDeleting}>
            Delete User
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
